import React, { useEffect, useState } from "react";
import { ArrowUp, ArrowDown } from "@phosphor-icons/react";

/**
 * v1.29.13 — Floating "Jump to Latest" button for long ledger / statement
 * pages. Sits fixed bottom-right, only appears when there are enough rows
 * to warrant scrolling AND the user has scrolled away from the end that
 * currently holds the newest entry.
 *
 *   direction = "desc" → newest entry is at the TOP → arrow ▲
 *   direction = "asc"  → newest entry is at the BOTTOM → arrow ▼
 *
 * `onJump` is called when the button is tapped. Parent decides how to
 * scroll (typically ref.scrollIntoView on the tbody top/bottom marker).
 */
export default function JumpToLatestButton({
    direction = "desc",
    onJump,
    rowCount = 0,
    minRows = 10,
    testId = "jump-to-latest",
    bottomOffsetClass = "bottom-24 sm:bottom-8",
    containerSelector = null, // v1.29.29 — Party Ledger's three-zone layout scrolls an inner zone, not the window; measure THAT container when provided
}) {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (rowCount < minRows) { setVisible(false); return; }
        const el = containerSelector ? document.querySelector(containerSelector) : null;
        const onScroll = () => {
            let scrollY, scrollBottom;
            if (el) {
                scrollY = el.scrollTop;
                scrollBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
            } else {
                scrollY = window.scrollY || window.pageYOffset || 0;
                scrollBottom =
                    (document.documentElement.scrollHeight || 0) -
                    (window.innerHeight || 0) -
                    scrollY;
            }
            if (direction === "desc") {
                setVisible(scrollY > 400);
            } else {
                setVisible(scrollBottom > 400);
            }
        };
        onScroll();
        const target = el || window;
        target.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            target.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [direction, rowCount, minRows, containerSelector]);
    if (!visible) return null;
    const Icon = direction === "desc" ? ArrowUp : ArrowDown;
    const title =
        direction === "desc"
            ? "Jump to newest entry (top of the list)"
            : "Jump to newest entry (bottom of the list)";
    return (
        <button
            type="button"
            onClick={onJump}
            data-testid={testId}
            title={title}
            aria-label={title}
            className={`fixed right-4 sm:right-6 ${bottomOffsetClass} z-40 inline-flex items-center gap-2 rounded-full border border-emerald-600/20 bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg ring-2 ring-white transition hover:bg-emerald-700 active:scale-95`}
        >
            <Icon size={16} weight="bold" />
            <span>Latest</span>
        </button>
    );
}
